import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { CompareProvider } from "./context/CompareContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CompareBar from "./components/CompareBar";
import BottomNav from "./components/BottomNav";
import IOSInstallBanner from "./components/IOSInstallBanner";
import ProtectedRoute from "./components/ProtectedRoute";

import Home from "./pages/Home";
import Properties from "./pages/Properties";
import PropertyDetail from "./pages/PropertyDetail";
import Auctions from "./pages/Auctions";
import AuctionDetail from "./pages/AuctionDetail";
import Brokers from "./pages/Brokers";
import PartnerProfile from "./pages/PartnerProfile";
import Advocates from "./pages/Advocates";
import LoanServices from "./pages/LoanServices";
import LegalServices from "./pages/LegalServices";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Pricing from "./pages/Pricing";
import Reviews from "./pages/Reviews";
import ReferAndEarn from "./pages/ReferAndEarn";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyOtp from "./pages/VerifyOtp";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import AddProperty from "./pages/AddProperty";
import AreaLandingPage from "./pages/AreaLandingPage";
import NagpurAreasIndex from "./pages/NagpurAreasIndex";
import Compare from "./pages/Compare";
import Services from "./pages/Services";
import Architects from "./pages/Architects";
import InteriorDesigners from "./pages/InteriorDesigners";
import CivilContractors from "./pages/CivilContractors";
import InvestmentGuides, { GuideDetail } from "./pages/InvestmentGuides";
import Calculators from "./pages/Calculators";
import Builders from "./pages/Builders";
import NewProjects from "./pages/NewProjects";
import AdminLogin from "./pages/AdminLogin";
import Notifications from "./pages/Notifications";
import Chat from "./pages/Chat";
import Profile from "./pages/Profile";
import MyProperties from "./pages/MyProperties";
import SavedProperties from "./pages/SavedProperties";
import MyInquiries from "./pages/MyInquiries";
import AccountSettings from "./pages/AccountSettings";
import FeatureProperty from "./pages/FeatureProperty";
import MyPortfolio from "./pages/MyPortfolio";
import MyServicesDashboard from "./pages/MyServicesDashboard";
import ServiceProfessional from "./pages/ServiceProfessional";
import Review from "./pages/Review";
import NotFound from "./pages/NotFound";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Wallet from "./pages/Wallet";
import Kyc from "./pages/Kyc";
import MyPayments from "./pages/MyPayments";
import MySubscriptions from "./pages/MySubscriptions";
import MyBoosts from "./pages/MyBoosts";
import MyReviews from "./pages/MyReviews";
import BoostHub from "./pages/BoostHub";
import DiscoverLocations from "./pages/DiscoverLocations";
import ReelsFeed from "./pages/ReelsFeed";
import ReelsCreate from "./pages/ReelsCreate";
import MyReels from "./pages/MyReels";
import AdminReels from "./pages/AdminReels";
import PartnerActivate from "./pages/PartnerActivate";
import PartnerDashboard from "./pages/PartnerDashboard";
import PartnerMarketingKit from "./pages/PartnerMarketingKit";
import MetaPageView from "./components/MetaPageView";

function Layout({ children }) {
    return (
        <div className="App">
            <Navbar />
            <main className="page-enter">{children}</main>
            <CompareBar />
            <BottomNav />
            <IOSInstallBanner />
            {/* Footer is hidden on mobile so pages end naturally at their last content element.
                Desktop keeps the full marketing footer for SEO & site-map value. */}
            <div className="hidden lg:block"><Footer /></div>
        </div>
    );
}

function App() {
    return (
        <AuthProvider>
            <CompareProvider>
                <BrowserRouter>
                    <Toaster theme="dark" richColors position="top-right" />
                    <MetaPageView />
                    <Routes>
                        <Route path="/" element={<Layout><Home /></Layout>} />
                        <Route path="/properties" element={<Layout><Properties /></Layout>} />
                        <Route path="/properties/:id" element={<Layout><PropertyDetail /></Layout>} />
                        <Route path="/compare" element={<Layout><Compare /></Layout>} />
                        <Route path="/services" element={<Layout><Services /></Layout>} />
                        <Route path="/architects" element={<Layout><Architects /></Layout>} />
                        <Route path="/interior-designers" element={<Layout><InteriorDesigners /></Layout>} />
                        <Route path="/civil-contractors" element={<Layout><CivilContractors /></Layout>} />
                        <Route path="/guides" element={<Layout><InvestmentGuides /></Layout>} />
                        <Route path="/guides/:slug" element={<Layout><GuideDetail /></Layout>} />
                        <Route path="/calculators" element={<Layout><Calculators /></Layout>} />
                        <Route path="/builders" element={<Layout><Builders /></Layout>} />
                        <Route path="/new-projects" element={<Layout><NewProjects /></Layout>} />
                        <Route path="/auctions" element={<Layout><Auctions /></Layout>} />
                        <Route path="/auctions/:id" element={<Layout><AuctionDetail /></Layout>} />
                        <Route path="/brokers" element={<Layout><Brokers /></Layout>} />
                        <Route path="/partners/:id" element={<Layout><PartnerProfile /></Layout>} />
                        <Route path="/advocates" element={<Layout><Advocates /></Layout>} />
                        <Route path="/loan-services" element={<Layout><LoanServices /></Layout>} />
                        <Route path="/legal-services" element={<Layout><LegalServices /></Layout>} />
                        <Route path="/about" element={<Layout><About /></Layout>} />
                        <Route path="/contact" element={<Layout><Contact /></Layout>} />
                        <Route path="/pricing" element={<Layout><Pricing /></Layout>} />
                        <Route path="/refer" element={<Layout><ReferAndEarn /></Layout>} />
                        <Route path="/partner/activate" element={<ProtectedRoute><Layout><PartnerActivate /></Layout></ProtectedRoute>} />
                        <Route path="/partner/dashboard" element={<ProtectedRoute><Layout><PartnerDashboard /></Layout></ProtectedRoute>} />
                        <Route path="/partner/marketing-kit" element={<ProtectedRoute><Layout><PartnerMarketingKit /></Layout></ProtectedRoute>} />
                        <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
                        <Route path="/terms" element={<Layout><Terms /></Layout>} />
                        <Route path="/refer-and-earn" element={<Layout><ReferAndEarn /></Layout>} />
                        <Route path="/reviews" element={<Layout><Reviews /></Layout>} />
                        <Route path="/property-in-nagpur" element={<Layout><NagpurAreasIndex /></Layout>} />
                        <Route path="/property-in-nagpur/:area" element={<Layout><AreaLandingPage /></Layout>} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/admin-login" element={<AdminLogin />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/verify-otp" element={<VerifyOtp />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <Layout><Dashboard /></Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/notifications"
                            element={
                                <ProtectedRoute>
                                    <Layout><Notifications /></Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/chat"
                            element={
                                <ProtectedRoute>
                                    <Layout><Chat /></Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/chat/:convId"
                            element={
                                <ProtectedRoute>
                                    <Layout><Chat /></Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/profile"
                            element={<Layout><Profile /></Layout>}
                        />
                        <Route
                            path="/my-properties"
                            element={<ProtectedRoute><Layout><MyProperties /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/my-posted-properties"
                            element={<ProtectedRoute><Layout><MyProperties posted /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/saved"
                            element={<ProtectedRoute><Layout><SavedProperties /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/my-inquiries"
                            element={<ProtectedRoute><Layout><MyInquiries /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/account-settings"
                            element={<ProtectedRoute><Layout><AccountSettings /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/feature-property/:id"
                            element={<ProtectedRoute><Layout><FeatureProperty /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/my-portfolio"
                            element={<ProtectedRoute><Layout><MyPortfolio /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/my-services"
                            element={<ProtectedRoute><Layout><MyServicesDashboard /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/wallet"
                            element={<ProtectedRoute><Layout><Wallet /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/kyc"
                            element={<ProtectedRoute><Layout><Kyc /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/my-payments"
                            element={<ProtectedRoute><Layout><MyPayments /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/my-subscriptions"
                            element={<ProtectedRoute><Layout><MySubscriptions /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/my-boosts"
                            element={<ProtectedRoute><Layout><MyBoosts /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/my-reviews"
                            element={<ProtectedRoute><Layout><MyReviews /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/boost"
                            element={<Layout><BoostHub /></Layout>}
                        />
                        {/* GPM Reels — full-screen feed, deep-link, create, manage */}
                        <Route path="/reels" element={<ReelsFeed />} />
                        <Route path="/reels/:reelId" element={<ReelsFeed />} />
                        <Route
                            path="/reels/create"
                            element={<ProtectedRoute><Layout><ReelsCreate /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/my-reels"
                            element={<ProtectedRoute><Layout><MyReels /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/admin/reels"
                            element={<ProtectedRoute roles={["admin"]}><Layout><AdminReels /></Layout></ProtectedRoute>}
                        />
                        <Route
                            path="/discover/property/:key"
                            element={<Layout><DiscoverLocations kind="property" /></Layout>}
                        />
                        <Route
                            path="/discover/service/:key"
                            element={<Layout><DiscoverLocations kind="service" /></Layout>}
                        />
                        <Route
                            path="/services/professional/:userId"
                            element={<Layout><ServiceProfessional /></Layout>}
                        />
                        <Route path="/review/:token" element={<Review />} />
                        <Route
                            path="/add-property"
                            element={
                                <ProtectedRoute>
                                    <Layout><AddProperty /></Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/edit-property/:id"
                            element={
                                <ProtectedRoute>
                                    <Layout><AddProperty /></Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route path="*" element={<Layout><NotFound /></Layout>} />
                    </Routes>
                </BrowserRouter>
            </CompareProvider>
        </AuthProvider>
    );
}

export default App;
